import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Bell, Trophy, Wallet, Calendar, CheckCheck, Trash2,
  RefreshCw, Search, CheckCircle, AlertCircle, Info, Filter, ArrowRight, ShieldCheck
} from 'lucide-react';
import { API_URL as API } from '../../config/api';

const ACCENT = '#CEF17B';
const ACCENT_DARK = '#084734';

export default function PartnerNotifications({ token, onTabChange, onToast }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // all, unread, tournament, booking, wallet, system

  const fetchNotifications = async (showSpinner = false) => {
    if (!token) return;
    if (showSpinner) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data?.notifications || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [token]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      if (onToast) onToast('Notification marked as read', 'success');
    } catch (err) {
      console.error('Failed to mark read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      if (onToast) onToast('All notifications marked as read', 'success');
    } catch (err) {
      console.error('Failed to mark all read:', err);
    }
  };

  const deleteNotif = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.delete(`${API}/notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (onToast) onToast('Notification deleted', 'success');
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    const date = new Date(isoStr);
    const now = new Date();
    const diffSec = Math.floor((now - date) / 1000);
    if (diffSec < 60) return 'Just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hr ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay} d ago`;
    return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getCategory = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('tournament')) return 'tournament';
    if (t.includes('booking')) return 'booking';
    if (t.includes('wallet') || t.includes('payout') || t.includes('payment')) return 'wallet';
    return 'system';
  };

  const getNotifDetails = (type) => {
    const cat = getCategory(type);
    switch (cat) {
      case 'tournament':
        return { icon: Trophy, color: '#059669', bg: '#D1FAE5', label: 'Tournament', targetTab: 'tournaments' };
      case 'booking':
        return { icon: Calendar, color: '#2563EB', bg: '#DBEAFE', label: 'Turf Booking', targetTab: 'bookings' };
      case 'wallet':
        return { icon: Wallet, color: '#7C3AED', bg: '#EDE9FE', label: 'Wallet & Payout', targetTab: 'wallet' };
      default:
        return { icon: Bell, color: '#D97706', bg: '#FEF3C7', label: 'System', targetTab: 'dashboard' };
    }
  };

  // Metrics
  const totalCount = notifications.length;
  const unreadCount = notifications.filter(n => !n.is_read).length;
  const tournamentCount = notifications.filter(n => getCategory(n.type) === 'tournament').length;
  const walletCount = notifications.filter(n => getCategory(n.type) === 'wallet').length;

  // Filtered Notifications
  const filteredNotifications = notifications.filter(n => {
    const matchesSearch =
      (n.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (n.message || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;

    if (activeFilter === 'unread') return !n.is_read;
    if (activeFilter === 'tournament') return getCategory(n.type) === 'tournament';
    if (activeFilter === 'booking') return getCategory(n.type) === 'booking';
    if (activeFilter === 'wallet') return getCategory(n.type) === 'wallet';
    if (activeFilter === 'system') return getCategory(n.type) === 'system';
    return true;
  });

  return (
    <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
      
      {/* ── HEADER TITLE & ACTIONS ── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem'
      }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#0D1F0F', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            Notifications & Alerts
            {unreadCount > 0 && (
              <span style={{
                background: '#EF4444', color: 'white', fontSize: '0.75rem', fontWeight: '800',
                padding: '2px 10px', borderRadius: '20px'
              }}>
                {unreadCount} Unread
              </span>
            )}
          </h1>
          <p style={{ margin: '4px 0 0', color: '#4B5563', fontSize: '0.88rem', fontWeight: '500' }}>
            Track tournament registrations, turf booking updates, and wallet payout releases in real time.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              style={{
                background: ACCENT_DARK, color: ACCENT, border: 'none',
                borderRadius: '10px', padding: '9px 16px', fontWeight: '700',
                fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                transition: 'transform 0.15s ease'
              }}
            >
              <CheckCheck size={16} /> Mark All as Read
            </button>
          )}

          <button
            onClick={() => fetchNotifications(true)}
            style={{
              background: '#FFFFFF', color: '#374151', border: '1px solid #E9EDE8',
              borderRadius: '10px', padding: '9px 14px', fontWeight: '700',
              fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
            }}
            title="Refresh notifications"
          >
            <RefreshCw size={15} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── METRIC STAT CARDS ── */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '1rem', marginBottom: '1.5rem'
      }}>
        {[
          { label: 'Total Alerts', count: totalCount, icon: Bell, color: '#2563EB', bg: '#EFF6FF' },
          { label: 'Unread Messages', count: unreadCount, icon: AlertCircle, color: '#D97706', bg: '#FEF3C7' },
          { label: 'Tournament Alerts', count: tournamentCount, icon: Trophy, color: '#059669', bg: '#ECFDF5' },
          { label: 'Wallet & Payouts', count: walletCount, icon: Wallet, color: '#7C3AED', bg: '#F5F3FF' },
        ].map((s, idx) => {
          const Icon = s.icon;
          return (
            <div key={idx} style={{
              background: '#FFFFFF', borderRadius: '16px', padding: '1.2rem 1.4rem',
              border: '1px solid #E9EDE8', boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                <div style={{ fontSize: '1.8rem', fontWeight: '900', color: '#0D1F0F', marginTop: '4px' }}>{s.count}</div>
              </div>
              <div style={{
                width: '46px', height: '46px', borderRadius: '14px', background: s.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Icon size={22} color={s.color} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ── FILTER & SEARCH CONTAINER ── */}
      <div style={{
        background: '#FFFFFF', borderRadius: '16px', padding: '1rem 1.2rem',
        border: '1px solid #E9EDE8', marginBottom: '1.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '1rem'
      }}>

        {/* Filter Pills */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflowX: 'auto', paddingBottom: '2px' }}>
          {[
            { id: 'all', label: `All (${totalCount})` },
            { id: 'unread', label: `Unread (${unreadCount})` },
            { id: 'tournament', label: '🏆 Tournaments' },
            { id: 'booking', label: '📅 Turf Bookings' },
            { id: 'wallet', label: '💰 Wallet & Payouts' },
            { id: 'system', label: '🔔 System' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              style={{
                background: activeFilter === f.id ? ACCENT_DARK : '#F4F6F3',
                color: activeFilter === f.id ? ACCENT : '#374151',
                border: 'none', borderRadius: '8px', padding: '6px 14px',
                fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer',
                whiteSpace: 'nowrap', transition: 'all 0.15s ease'
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Search Input */}
        <div style={{ position: 'relative', minWidth: '240px', flex: 1, maxWidth: '340px' }}>
          <Search size={16} color="#9CA3AF" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px 8px 36px',
              border: '1px solid #E5E7EB', borderRadius: '10px',
              fontSize: '0.85rem', fontWeight: '500', outline: 'none',
              background: '#FAFAFA'
            }}
          />
        </div>
      </div>

      {/* ── NOTIFICATIONS LIST ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {loading ? (
          <div style={{
            background: '#FFFFFF', borderRadius: '16px', padding: '3rem',
            textAlign: 'center', border: '1px solid #E9EDE8', color: '#6B7280'
          }}>
            <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
            <p style={{ fontWeight: '600', margin: 0 }}>Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div style={{
            background: '#FFFFFF', borderRadius: '16px', padding: '3.5rem 1.5rem',
            textAlign: 'center', border: '1px solid #E9EDE8'
          }}>
            <div style={{
              width: '54px', height: '54px', borderRadius: '50%', background: '#F4F6F3',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem'
            }}>
              <Bell size={24} color="#9CA3AF" />
            </div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#111827', margin: '0 0 6px' }}>
              No notifications found
            </h3>
            <p style={{ color: '#6B7280', fontSize: '0.85rem', margin: 0, maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
              {searchQuery ? `No alerts matched "${searchQuery}". Try clearing your search.` : 'You have no notifications in this category.'}
            </p>
          </div>
        ) : (
          filteredNotifications.map(n => {
            const meta = getNotifDetails(n.type);
            const Icon = meta.icon;
            const isUnread = !n.is_read;

            return (
              <div
                key={n.id}
                onClick={() => isUnread && markAsRead(n.id)}
                style={{
                  background: isUnread ? '#F0FDF4' : '#FFFFFF',
                  borderRadius: '16px', padding: '1.2rem 1.4rem',
                  border: isUnread ? '1.5px solid #86EFAC' : '1px solid #E9EDE8',
                  boxShadow: isUnread ? '0 4px 14px rgba(16,185,129,0.08)' : '0 1px 4px rgba(0,0,0,0.02)',
                  display: 'flex', gap: '14px', alignItems: 'flex-start',
                  cursor: isUnread ? 'pointer' : 'default',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
              >
                {/* Category Icon */}
                <div style={{
                  width: '42px', height: '42px', borderRadius: '12px',
                  background: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, marginTop: '2px'
                }}>
                  <Icon size={20} color={meta.color} />
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        background: meta.bg, color: meta.color,
                        fontSize: '0.68rem', fontWeight: '800',
                        padding: '2px 8px', borderRadius: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'
                      }}>
                        {meta.label}
                      </span>
                      <h4 style={{
                        margin: 0, fontSize: '0.95rem', fontWeight: isUnread ? '800' : '700',
                        color: isUnread ? '#064E3B' : '#111827'
                      }}>
                        {n.title}
                      </h4>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: '600', color: isUnread ? '#059669' : '#9CA3AF' }}>
                        {formatTime(n.created_at)}
                      </span>
                      {isUnread && (
                        <span style={{
                          width: '9px', height: '9px', borderRadius: '50%', background: '#10B981',
                          display: 'inline-block'
                        }} title="Unread notification" />
                      )}
                    </div>
                  </div>

                  <p style={{
                    margin: '6px 0 0', fontSize: '0.88rem', color: isUnread ? '#065F46' : '#4B5563',
                    lineHeight: '1.5', fontWeight: '400', wordBreak: 'break-word'
                  }}>
                    {n.message}
                  </p>

                  {/* Actions / Navigation link if applicable */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '12px' }}>
                    {meta.targetTab && onTabChange && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isUnread) markAsRead(n.id);
                          onTabChange(meta.targetTab);
                        }}
                        style={{
                          background: 'transparent', border: 'none', padding: 0,
                          color: ACCENT_DARK, fontWeight: '700', fontSize: '0.8rem',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
                        }}
                      >
                        View in {meta.label} <ArrowRight size={13} />
                      </button>
                    )}

                    {!isUnread && (
                      <span style={{ fontSize: '0.75rem', color: '#9CA3AF', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <CheckCircle size={12} color="#10B981" /> Read
                      </span>
                    )}

                    <button
                      onClick={(e) => deleteNotif(n.id, e)}
                      style={{
                        background: 'transparent', border: 'none', padding: 0,
                        color: '#9CA3AF', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                        fontSize: '0.78rem', fontWeight: '500', marginLeft: 'auto',
                        transition: 'color 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#EF4444'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
                      title="Delete notification"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

    </div>
  );
}
